// TZ-684 — the single source of truth for multilingual support: languages,
// path logic and per-page translation coverage.
//
// Consumers:
//   - src/i18n/index.ts               → i18next initialisation + helper re-exports
//   - src/components/Layout.tsx       → <html lang>, og:locale, hreflang
//   - src/components/LanguageSwitcher → link target (or graceful degradation when a version is missing)
//   - gatsby-config.js (sitemap)      → <xhtml:link rel="alternate">
//
// CJS, because gatsby-config.js runs as CJS at build time and requires this
// file directly. TS files import it with a plain `import` (TS handles that
// transparently) — which keeps the path logic in ONE place.
// PR #39 duplicated normalizePlPath in both TS and CJS; we do not repeat that here.

// Order matters: the first element is the default language (no prefix).
const SUPPORTED_LANGUAGES = ["pl", "en", "uk"]
const DEFAULT_LANGUAGE = "pl"

// Languages that appear in the URL as a prefix: /en/..., /uk/...
const PREFIXED_LANGUAGES = SUPPORTED_LANGUAGES.filter(
  (lang) => lang !== DEFAULT_LANGUAGE
)

// og:locale requires the language_COUNTRY form, not the bare language code.
const OG_LOCALE = {
  pl: "pl_PL",
  en: "en_US",
  uk: "uk_UA",
}

// TRANSLATION COVERAGE: canonical PL path → the languages this page ACTUALLY
// exists in (excluding the default language, which is always there).
//
// The "honest hreflang" principle: we add an entry ONLY once the page file
// genuinely exists. Empty coverage = no hreflang, no sitemap alternates, the
// language switcher hidden. A language version reported to Google that returns
// a 404 is worse than not having one at all.
//
// How to add a page: create src/pages/<lang>/<path>/index.tsx, add an entry
// here and run `npm run validate:i18n`. Hreflang, the sitemap and the switcher
// then light up on their own.
const COVERAGE_WHEN_PUBLIC = {
  // ⚠ Strona główna TYLKO po angielsku. Po przebudowie na komponenty
  // cityLanding jej treść siedzi w słowniku `cityLanding`, a ten po ukraińsku
  // jest celowo pusty — i18next spadał na polski i ukraińska strona główna
  // pokazywała polskie nagłówki. To ta sama zasada, dla której landingi
  // miejskie mają tu wyłącznie ["en"]: lepiej nie mieć wersji, niż mieć wersję
  // po polsku pod ukraińskim adresem.
  //
  // Wpis wraca razem z przekładem cityLanding na ukraiński i z odtworzeniem
  // src/pages/uk/index.tsx — nigdy wcześniej.
  "/": ["en"],
  "/kontakt/": ["en", "uk"],
  // Landingi dermatoskopowe: TYLKO ["en"]. Ukraiński słownik cityLanding jest
  // pusty — wpisanie tu "uk" obiecałoby w hreflangu stronę, której nie ma,
  // a bramka noPolishOnForeignPages złapałaby polski pod ukraińskim adresem.
  //
  // Ośrodków legacy (Łódź, Gdynia, Bydgoszcz, Żywiec) tu nie ma i mieć nie
  // będzie: placówki nie działają, więc angielska wersja ściągałaby ruch na
  // stronę bez rezerwacji.
  "/warszawa/dermatoskopowe-badanie-znamion-wideodermatoskopia/": ["en"],
  "/poznan/dermatoskopowe-badanie-znamion-wideodermatoskopia/": ["en"],
  "/wroclaw/dermatoskopowe-badanie-znamion-wideodermatoskopia/": ["en"],
  "/krakow/dermatoskopowe-badanie-znamion-wideodermatoskopia/": ["en"],
  "/szczecin/dermatoskopowe-badanie-znamion-wideodermatoskopia/": ["en"],
  "/gdansk/dermatoskopowe-badanie-znamion-wideodermatoskopia/": ["en"],
  "/katowice/dermatoskopowe-badanie-znamion-wideodermatoskopia/": ["en"],
  "/lublin/dermatoskopowe-badanie-znamion-wideodermatoskopia/": ["en"],
  "/krakow/usuwanie-znamion-pieprzykow/": ["en", "uk"],
  "/krakow/laserowe-usuwanie-znamion-pieprzykow-wlokniakow-brodawek-kurzajek/": [
    "en",
    "uk",
  ],
  "/poznan/usuwanie-znamion-pieprzykow/": ["en", "uk"],
  "/poznan/laserowe-usuwanie-znamion-pieprzykow-wlokniakow-brodawek-kurzajek/": ["en", "uk"],
  "/wroclaw/usuwanie-znamion-pieprzykow/": ["en", "uk"],
  "/wroclaw/laserowe-usuwanie-znamion-pieprzykow-wlokniakow-brodawek-kurzajek/": ["en", "uk"],
  "/szczecin/usuwanie-znamion-pieprzykow/": ["en", "uk"],
  "/szczecin/laserowe-usuwanie-znamion-pieprzykow-wlokniakow-brodawek-kurzajek/": ["en", "uk"],
  "/gdansk/usuwanie-znamion-pieprzykow/": ["en", "uk"],
  "/gdansk/laserowe-usuwanie-znamion-pieprzykow-wlokniakow-brodawek-kurzajek/": ["en", "uk"],
  "/lublin/usuwanie-znamion-pieprzykow/": ["en", "uk"],
  "/lublin/laserowe-usuwanie-znamion-pieprzykow-wlokniakow-brodawek-kurzajek/": ["en", "uk"],
  "/katowice/usuwanie-znamion-pieprzykow/": ["en", "uk"],
  "/warszawa/usuwanie-znamion-pieprzykow/": ["en", "uk"],
  "/warszawa/laserowe-usuwanie-znamion-pieprzykow-wlokniakow-brodawek-kurzajek/": ["en", "uk"],
  "/o-nas/": ["en", "uk"],
  // Cennik zbiorczy nie pokazuje żadnej ceny — rozgałęzia do cennika miejskiego,
  // a te są przetłumaczone w słownikach `city`. Wersji ukraińskiej nie ma, bo
  // ukraiński jest wygaszony; wpis dojdzie razem z nim.
  "/cennik/": ["en"],
  // Bon podarunkowy razem z regulaminem. ⚠ Przyciski „Buy now" prowadzą do
  // Przelewów24, które mówią wyłącznie po polsku — angielska jest strona, nie
  // płatność.
  "/bon-podarunkowy/": ["en"],
  // Dokumenty formalne mają samodzielne wersje angielskie. Treść pozostaje
  // równoległa do polskich dokumentów, a strony nie korzystają z medycznych
  // bloków konwersyjnych szablonu artykułu.
  "/polityka/": ["en"],
  "/regulamin/": ["en"],
  // Obie strony onkologiczne miały wersje /en/ od commita 730f2c5, ale nigdy nie
  // trafiły do pokrycia. Przy wygaszonych językach nie robiło to różnicy —
  // wypadały z sitemapy razem z resztą. Po zapaleniu angielskiego zostałyby w
  // sitemapie jako sieroty: zaindeksowane, bez hreflanga do polskiego oryginału
  // i bez przełącznika, czyli jako samodzielny duplikat. Zweryfikowane w
  // zbudowanym HTML — obie renderują się po angielsku (h1 i title).
  "/konsultacje-onkologiczne-nowotwory-skory/": ["en"],
  "/poznan/konsultacje-onkologiczne-leczenie-czerniaka/": ["en"],
}

// 🔴 WYGASZENIE JĘZYKÓW — per język, nie zero-jedynkowo dla wszystkich naraz.
//
// Tu wpisujemy języki OPRÓCZ domyślnego, które są publiczne. Pusta lista = pełne
// wygaszenie (stan sprzed sierpnia 2026), ["en"] = angielski żyje, ukraiński
// nadal się buduje, ale nic do niego nie linkuje i wypada z sitemapy.
//
// Wygaszenie działa przez COVERAGE, bo wszystkie wejścia językowe czytają je
// przez languagesForPath()/hasLanguage(): przełącznik języka (chowa się przy
// <2 wersjach), baner sugestii, hreflang i og:locale w Layout, alternates w
// sitemapie. Strony wygaszonego języka nadal się budują — dostają tylko noindex
// (Layout) i wypadnięcie z sitemapy (gatsby-config).
//
// ⚠ Sitemap wypisuje każdą stronę osobno, niezależnie od COVERAGE — dlatego
// wygaszeniu MUSI towarzyszyć wykluczenie w gatsby-config.js. Tamta lista jest
// TERAZ wyprowadzana z HIDDEN_LANGUAGES, więc nie ma czego pamiętać ręcznie.
//
// ⚠ Dług, który zapala się razem z językiem: loader appt-now
// (static.appt-now.pl/script.js) nie czyta atrybutu data-lng, więc formularz
// rezerwacji na /en/ startuje po polsku. Obietnica urywa się w miejscu
// konwersji — do naprawy po stronie widget#186 / widget-scripts#7 / core#815.
const PUBLIC_LANGUAGES = ["en"]

const HIDDEN_LANGUAGES = PREFIXED_LANGUAGES.filter(
  (lang) => !PUBLIC_LANGUAGES.includes(lang)
)

const isLanguagePublic = (language) =>
  language === DEFAULT_LANGUAGE || PUBLIC_LANGUAGES.includes(language)

// Pokrycie przycięte do języków publicznych. Wpis, z którego nic nie zostaje,
// znika — languagesForPath() zwraca wtedy samo ["pl"] i przełącznik się chowa.
const COVERAGE = Object.entries(COVERAGE_WHEN_PUBLIC).reduce(
  (acc, [plPath, languages]) => {
    const visible = languages.filter((lang) => PUBLIC_LANGUAGES.includes(lang))
    if (visible.length) acc[plPath] = visible
    return acc
  },
  {}
)

// Strips the language prefix and normalises to the canonical PL path with a
// trailing slash. /en/o-nas → /o-nas/ ; /uk → / ; /kontakt → /kontakt/
const normalizePlPath = (pathname) => {
  if (!pathname) return "/"
  let path = pathname.startsWith("/") ? pathname : `/${pathname}`

  for (const lang of PREFIXED_LANGUAGES) {
    if (path === `/${lang}` || path === `/${lang}/`) return "/"
    if (path.startsWith(`/${lang}/`)) {
      path = path.slice(lang.length + 1)
      break
    }
  }

  if (path !== "/" && !path.endsWith("/")) path = `${path}/`
  return path
}

// The language follows SOLELY from the URL — no localStorage, no cookies, no
// Accept-Language. A banner may suggest switching, but the language of the
// rendered content always matches the URL the user is on (an SEO requirement:
// one URL = one stable language version for the crawler).
const getLanguageFromPath = (pathname) => {
  if (!pathname) return DEFAULT_LANGUAGE
  const path = pathname.startsWith("/") ? pathname : `/${pathname}`

  for (const lang of PREFIXED_LANGUAGES) {
    if (path === `/${lang}` || path.startsWith(`/${lang}/`)) return lang
  }
  return DEFAULT_LANGUAGE
}

// Builds the path to the same page in a given language. Idempotent — an
// already-prefixed path may be passed in.
const buildLanguagePath = (pathname, language) => {
  const plPath = normalizePlPath(pathname)
  if (language === DEFAULT_LANGUAGE) return plPath
  if (plPath === "/") return `/${language}/`
  return `/${language}${plPath}`
}

// Every language this page exists in — with the default language first.
// For a page with no translations this returns the single-element array ["pl"].
const languagesForPath = (pathname) => {
  const translated = COVERAGE[normalizePlPath(pathname)] || []
  return [DEFAULT_LANGUAGE, ...translated]
}

const hasLanguage = (pathname, language) =>
  languagesForPath(pathname).includes(language)

module.exports = {
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  PREFIXED_LANGUAGES,
  OG_LOCALE,
  COVERAGE,
  // Zakres docelowy, niezależny od wygaszenia — walidator sprawdza JEGO, żeby
  // wygaszenie nie zamieniło bramki spójności w bramkę zawsze-zieloną.
  COVERAGE_WHEN_PUBLIC,
  PUBLIC_LANGUAGES,
  HIDDEN_LANGUAGES,
  isLanguagePublic,
  normalizePlPath,
  getLanguageFromPath,
  buildLanguagePath,
  languagesForPath,
  hasLanguage,
}
